import { useState, useRef, useCallback } from "react";

interface Position {
  x: number;
  y: number;
}

interface UsePanPinchZoomOptions {
  initialZoom?: number;
  minZoom?: number;
  maxZoom?: number;
  zoomStep?: number;
  rotation?: number;
}

export const usePanPinchZoom = ({
  initialZoom = 100,
  minZoom = 100,
  maxZoom = 500,
  zoomStep = 100,
  rotation = 0,
}: UsePanPinchZoomOptions = {}) => {
  const [zoomLevel, setZoomLevel] = useState(initialZoom);
  const [panPosition, setPanPosition] = useState<Position>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const dragStartRef = useRef<Position>({ x: 0, y: 0 });
  const panStartRef = useRef<Position>({ x: 0, y: 0 });
  const initialPinchDistanceRef = useRef<number>(0);
  const initialPinchZoomRef = useRef<number>(initialZoom);

  const zoomScale = zoomLevel / 100;
  const isZoomed = zoomLevel > minZoom;

  const adjustPanForRotation = useCallback(
    (deltaX: number, deltaY: number) => {
      if (rotation === 90) return { x: deltaY, y: -deltaX };
      if (rotation === 180) return { x: -deltaX, y: -deltaY };
      if (rotation === 270) return { x: -deltaY, y: deltaX };
      return { x: deltaX, y: deltaY };
    },
    [rotation]
  );

  const resetZoom = useCallback(() => {
    setZoomLevel(minZoom);
    setPanPosition({ x: 0, y: 0 });
  }, [minZoom]);

  const zoomIn = useCallback(() => {
    setZoomLevel((prev) => Math.min(maxZoom, prev + zoomStep));
  }, [maxZoom, zoomStep]);

  const zoomOut = useCallback(() => {
    const newZoom = Math.max(minZoom, zoomLevel - zoomStep);
    if (newZoom === minZoom) {
      resetZoom();
    } else {
      setZoomLevel(newZoom);
    }
  }, [minZoom, zoomLevel, zoomStep, resetZoom]);

  const startDrag = useCallback(
    (clientX: number, clientY: number) => {
      if (!isZoomed) return;
      setIsDragging(true);
      dragStartRef.current = { x: clientX, y: clientY };
      panStartRef.current = { x: panPosition.x, y: panPosition.y };
    },
    [isZoomed, panPosition]
  );

  const updateDrag = useCallback(
    (clientX: number, clientY: number) => {
      if (!isDragging || !isZoomed) return;
      const screenDeltaX = clientX - dragStartRef.current.x;
      const screenDeltaY = clientY - dragStartRef.current.y;
      const adjustedDelta = adjustPanForRotation(screenDeltaX, screenDeltaY);
      setPanPosition({
        x: panStartRef.current.x + adjustedDelta.x,
        y: panStartRef.current.y + adjustedDelta.y,
      });
    },
    [isDragging, isZoomed, adjustPanForRotation]
  );

  const endDrag = useCallback(() => {
    setIsDragging(false);
  }, []);

  const startPinch = useCallback(
    (distance: number) => {
      initialPinchDistanceRef.current = distance;
      initialPinchZoomRef.current = zoomLevel;
    },
    [zoomLevel]
  );

  const updatePinch = useCallback(
    (distance: number) => {
      if (initialPinchDistanceRef.current === 0) return;

      const scale = distance / initialPinchDistanceRef.current;
      const calculatedZoom = initialPinchZoomRef.current * scale;
      const newZoom = Math.round(
        Math.min(maxZoom, Math.max(minZoom, calculatedZoom))
      );

      if (newZoom === minZoom) {
        resetZoom();
      } else {
        setZoomLevel(newZoom);
      }
    },
    [minZoom, maxZoom, resetZoom]
  );

  const endPinch = useCallback(() => {
    initialPinchDistanceRef.current = 0;
  }, []);

  return {
    zoomLevel,
    zoomScale,
    isZoomed,
    panPosition,
    isDragging,
    zoomIn,
    zoomOut,
    startDrag,
    updateDrag,
    endDrag,
    startPinch,
    updatePinch,
    endPinch,
    resetZoom,
  };
};
